import {
  Box as BoxDS,
  Notification,
  Typography as TypographyDS,
} from '@cambridgeassessment/cambridge-ui';
import { Box, Button, CircularProgress } from '@material-ui/core';
import { styled } from '@material-ui/styles';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  approveRegistration,
  rejectRegistration,
} from 'src/services/arlo/arlo-service';
import { RegistrationItem } from '../../../types';
import OverflowTooltip from '../../OverflowTooltip/OverflowTooltip';
import Table from '../../Table/Table';
import { Order, Settings as TableSettings } from '../../Table/types';
import { useBookingsStore } from './bookingsStore';
import ConfirmationModal from './ConfirmationModal';
import { useProfessionalDevelopmentApprovalsStyles } from './styles';
import {
  Columns,
  headCellsApproved,
  headCellsPending,
  headCellsRejected,
  mapWorkflowNotification,
  Row,
  TabPanelProps,
} from './utils';

interface BookingsTableProps {
  value: number;
  noResultsMessage: React.JSX.Element;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
  >
    {value === index && <Box>{children}</Box>}
  </div>
);

const EMPTY_STATE_MIN_HEIGHT = 560;

const StyledBox = styled(Box)({
  overflowX: 'auto' as const,
  '& td': {
    textAlign: 'left' as const,
  },
  '& tbody tr td .MuiBox-root': {
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  '& td:has([data-testid="tableMessage-noData"])': {
    textAlign: 'center',
    verticalAlign: 'middle',
    height: EMPTY_STATE_MIN_HEIGHT,
  },
  '& td:has([data-testid="tableMessage-noData"]) > .MuiBox-root': {
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    minHeight: EMPTY_STATE_MIN_HEIGHT,
  },
  '& [data-testid="tableMessage-noData"]': {
    width: '100%',
    maxWidth: 640,
    margin: '0 auto',
    padding: '0 24px',
    boxSizing: 'border-box',
  },
  '& [data-testid="tableMessage-noData"] .MuiBox-root': {
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    width: '100%',
  },
  '& .MuiTablePagination-input': {
    width: 'auto',
    minWidth: 64,
  },
  '& .MuiTablePagination-select': {
    textAlign: 'left' as const,
    textAlignLast: 'left' as const,
  },
  '& .MuiTablePagination-root': {
    width: '100%',
  },
  '& .MuiTablePagination-displayedRows': {
    display: 'inline-flex',
    alignItems: 'center',
  },
  '& td > p': {
    whiteSpace: 'normal' as const,
    overflowWrap: 'anywhere' as const,
  },
  '& td > .MuiBox-root > p': {
    whiteSpace: 'normal' as const,
    overflowWrap: 'anywhere' as const,
  },
});

const createRows = (
  data: RegistrationItem[],
  isPending: boolean,
  classes: any,
  processingId: number | null,
  handleApproveClick: (registrationId: number) => void,
  handleRejectClick: (registrationId: number) => void,
  isRejected = false
): Row[] =>
  data.map((row) => ({
    cells: {
      ...(!isRejected && {
        [Columns.OrderCode]: (
          <OverflowTooltip variant="body1">{row.orderCode}</OverflowTooltip>
        ),
      }),
      [Columns.Organisation]: (
        <BoxDS sx={{ display: 'flex', flexDirection: 'column' }}>
          <OverflowTooltip variant="body1">
            {row.attendeeOrganizationName}
          </OverflowTooltip>
          <TypographyDS variant="caption">
            {row.attendeeOrganization}
          </TypographyDS>
        </BoxDS>
      ),
      [Columns.CourseName]: (
        <OverflowTooltip variant="body1">{row.course}</OverflowTooltip>
      ),
      ...(!isRejected && {
        [Columns.Cost]: (
          <OverflowTooltip variant="body1">{row.cost}</OverflowTooltip>
        ),
      }),
      [Columns.Attendee]: (
        <BoxDS sx={{ display: 'flex', flexDirection: 'column' }}>
          <OverflowTooltip variant="body1">{row.attendeeName}</OverflowTooltip>
          <TypographyDS variant="caption">{row.attendeeEmail}</TypographyDS>
        </BoxDS>
      ),
      ...(isPending && {
        [Columns.Action]: (
          <BoxDS sx={{ display: 'flex', alignItems: 'center' }}>
            {processingId === row.registrationId ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  className={classes.approveButton}
                  onClick={() => handleApproveClick(row.registrationId)}
                  disabled={processingId !== null}
                  data-testid={`approve-btn-${row.registrationId}`}
                >
                  Approve
                </Button>
                <Button
                  color="secondary"
                  size="small"
                  className={classes.rejectButton}
                  onClick={() => handleRejectClick(row.registrationId)}
                  disabled={processingId !== null}
                  data-testid={`reject-btn-${row.registrationId}`}
                >
                  Reject
                </Button>
              </>
            )}
          </BoxDS>
        ),
      }),
    },
  }));

const BookingsTable: React.FC<BookingsTableProps> = ({
  value,
  noResultsMessage,
}) => {
  const pendingRegistrations = useBookingsStore((state) => state.pending);
  const approvedRegistrations = useBookingsStore((state) => state.approved);
  const rejectedRegistrations = useBookingsStore((state) => state.rejected);
  const pendingTotalCount = useBookingsStore(
    (state) => state.pendingTotalCount
  );
  const approvedTotalCount = useBookingsStore(
    (state) => state.approvedTotalCount
  );
  const rejectedTotalCount = useBookingsStore(
    (state) => state.rejectedTotalCount
  );
  const pendingFullyLoaded = useBookingsStore(
    (state) => state.pendingFullyLoaded
  );
  const approvedFullyLoaded = useBookingsStore(
    (state) => state.approvedFullyLoaded
  );
  const rejectedFullyLoaded = useBookingsStore(
    (state) => state.rejectedFullyLoaded
  );

  const pendingBackgroundInFlight = useBookingsStore(
    (state) => state.pendingBackgroundInFlight
  );
  const approvedBackgroundInFlight = useBookingsStore(
    (state) => state.approvedBackgroundInFlight
  );
  const rejectedBackgroundInFlight = useBookingsStore(
    (state) => state.rejectedBackgroundInFlight
  );
  const isRefetching = useBookingsStore((state) => state.isRefetching);
  const refetch = useBookingsStore((state) => state.refetch);

  const [processingId, setProcessingId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    id: number;
    type: 'approve' | 'reject';
  } | null>(null);
  const [tableNotification, setTableNotification] = useState<{
    message: string;
    variant: 'success' | 'warning' | 'error';
  } | null>(null);

  const [pendingTableSettings, setPendingTableSettings] =
    useState<TableSettings>({
      order: Order.Asc,
      orderBy: '',
      page: 0,
      rowsPerPage: 10,
    });
  const [approvedTableSettings, setApprovedTableSettings] =
    useState<TableSettings>({
      order: Order.Asc,
      orderBy: '',
      page: 0,
      rowsPerPage: 10,
    });

  const [rejectedTableSettings, setRejectedTableSettings] =
    useState<TableSettings>({
      order: Order.Asc,
      orderBy: '',
      page: 0,
      rowsPerPage: 10,
    });

  const classes = useProfessionalDevelopmentApprovalsStyles();

  useEffect(() => {
    if (!tableNotification) return;

    const timer = setTimeout(() => {
      setTableNotification(null);
    }, 8000); // 8 seconds

    return () => clearTimeout(timer);
  }, [tableNotification]);

  const handleApproveClick = useCallback((registrationId: number) => {
    setPendingAction({ id: registrationId, type: 'approve' });
    setModalOpen(true);
  }, []);

  const handleRejectClick = useCallback((registrationId: number) => {
    setPendingAction({ id: registrationId, type: 'reject' });
    setModalOpen(true);
  }, []);

  const handleModalCancel = useCallback(() => {
    setModalOpen(false);
    setPendingAction(null);
  }, []);

  const handleModalConfirm = useCallback(async () => {
    if (!pendingAction) return;
    const { id, type } = pendingAction;
    setModalOpen(false);
    setPendingAction(null);
    setProcessingId(id);
    setTableNotification(null);

    try {
      if (type === 'approve') {
        const response = await approveRegistration(id.toString());
        setTableNotification(mapWorkflowNotification(response, 'approve'));
      } else {
        const response = await rejectRegistration(id.toString());
        setTableNotification(mapWorkflowNotification(response, 'reject'));
      }
      await refetch();
    } catch (error) {
      setTableNotification(mapWorkflowNotification(error, type, true));
    } finally {
      setProcessingId(null);
    }
  }, [pendingAction, refetch]);

  const pendingRows = useMemo(
    () =>
      createRows(
        pendingRegistrations,
        true,
        classes,
        processingId,
        handleApproveClick,
        handleRejectClick
      ),
    [
      pendingRegistrations,
      classes,
      processingId,
      handleApproveClick,
      handleRejectClick,
    ]
  );

  const approvedRows = useMemo(
    () =>
      createRows(
        approvedRegistrations,
        false,
        classes,
        processingId,
        handleApproveClick,
        handleRejectClick
      ),
    [
      approvedRegistrations,
      classes,
      processingId,
      handleApproveClick,
      handleRejectClick,
    ]
  );

  const rejectedRows = useMemo(
    () =>
      createRows(
        rejectedRegistrations,
        false,
        classes,
        processingId,
        handleApproveClick,
        handleRejectClick,
        true
      ),
    [
      rejectedRegistrations,
      classes,
      processingId,
      handleApproveClick,
      handleRejectClick,
      true,
    ]
  );

  return (
    <>
      <ConfirmationModal
        open={modalOpen}
        action={pendingAction?.type ?? 'approve'}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
      />

      {tableNotification && (
        <Box mb={2}>
          <Notification
            variant={tableNotification.variant}
            title={tableNotification.message}
            size="small"
            closeable
            onClose={() => setTableNotification(null)}
          />
        </Box>
      )}

      {isRefetching && (
        <Box display="flex" justifyContent="center" mb={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      <TabPanel value={value} index={0}>
        <StyledBox data-testid="pending-approvals-table">
          <Table
            headCells={headCellsPending}
            rows={pendingRows}
            settings={pendingTableSettings}
            updateSettings={setPendingTableSettings}
            noResultsMessage={noResultsMessage}
            rowsPerPageOptions={[10, 25, 50]}
            serverTotalCount={pendingTotalCount}
            loadingMore={!pendingFullyLoaded || pendingBackgroundInFlight}
            showEmptyRows={false}
          />
        </StyledBox>
      </TabPanel>

      <TabPanel value={value} index={1}>
        <StyledBox data-testid="approved-approvals-table">
          <Table
            headCells={headCellsApproved}
            rows={approvedRows}
            settings={approvedTableSettings}
            updateSettings={setApprovedTableSettings}
            noResultsMessage={noResultsMessage}
            rowsPerPageOptions={[10, 25, 50]}
            serverTotalCount={approvedTotalCount}
            loadingMore={!approvedFullyLoaded || approvedBackgroundInFlight}
            showEmptyRows={false}
          />
        </StyledBox>
      </TabPanel>

      <TabPanel value={value} index={2}>
        <StyledBox data-testid="rejected-approvals-table">
          <Table
            headCells={headCellsRejected}
            rows={rejectedRows}
            settings={rejectedTableSettings}
            updateSettings={setRejectedTableSettings}
            noResultsMessage={noResultsMessage}
            rowsPerPageOptions={[10, 25, 50]}
            serverTotalCount={rejectedTotalCount}
            loadingMore={!rejectedFullyLoaded || rejectedBackgroundInFlight}
            showEmptyRows={false}
          />
        </StyledBox>
      </TabPanel>
    </>
  );
};

export default memo(BookingsTable);
